import { buildDataTableCols, type DataTableCol, type DataTableColumn } from '@/components/custom/erp/data-table-v2';
import { formatDate, formatMoney, formatNumber, formatRoundedNumber } from '@/lib/formatUtils';
import { buildInputTableCols } from '@/components/custom/erp/input-table-v2';

type HoaDonMuaDichVuRow = MuaHangRecord;

export const DATA_TABLE_BASE_COLS: DataTableCol<HoaDonMuaDichVuRow>[] = [
  {
    field: 'status',
    headerName: 'Trạng thái',
    width: 120,
    valueGetter: row => row?.status,
    valueFormatter: value => {
      const status = String(value ?? '');
      switch (status) {
        case '0':
          return 'Lập chứng từ';
        case '3':
          return 'Chờ duyệt';
        case '5':
          return 'Xuất hóa đơn';
        case '6':
          return 'Đã xuất hóa đơn điện tử';
        case '7':
          return 'Bỏ duyệt đơn hàng';
        default:
          return '';
      }
    },
    filter: {
      type: 'multiple',
      options: [
        { value: '0', label: 'Lập chứng từ' },
        { value: '3', label: 'Chờ duyệt' },
        { value: '5', label: 'Xuất hóa đơn' },
        { value: '6', label: 'Đã xuất hóa đơn điện tử' },
        { value: '7', label: 'Bỏ duyệt đơn hàng' }
      ]
    }
  },
  {
    field: 'so_ct',
    headerName: 'Số c/từ',
    width: 140
  },
  {
    field: 'ngay_ct',
    headerName: 'Ngày c/từ',
    width: 120,
    valueGetter: row => row?.ngay_ct,
    valueFormatter: value => formatDate(value as MuaHangRecord),
    filter: { type: 'date' }
  },
  {
    field: 'so_ct0',
    headerName: 'Số hóa đơn',
    width: 120
  },
  {
    field: 'ma_kh',
    headerName: 'Mã khách hàng',
    width: 120,
    valueGetter: row => row?.ma_kh_data?.customer_code || row?.ma_kh || ''
  },
  {
    field: 'ten_kh',
    headerName: 'Tên khách hàng',
    width: 200,
    valueGetter: row => row?.ma_kh_data?.customer_name || row.ten_kh || ''
  },
  {
    field: 'dien_giai',
    headerName: 'Diễn giải',
    width: 200
  },
  {
    field: 'tk',
    headerName: 'Tk có',
    width: 100,
    valueGetter: row => row?.tk_data?.code || row?.tk || ''
  },
  {
    field: 't_tt_nt',
    headerName: 'Tổng tiền',
    width: 150,
    columnType: 'number',
    align: 'right',
    valueGetter: row => row?.t_tt_nt,
    valueFormatter: value => formatRoundedNumber(Number(value ?? 0)),
    filter: { type: 'text' }
  },
  {
    field: 'ma_nt',
    headerName: 'Ngoại tệ',
    width: 120,
    align: 'center',
    valueGetter: row => row?.ma_nt_data?.ma_nt || row?.ma_nt || ''
  }
];

export const getDataTableColumns = (
  handleViewClick: (row: MuaHangRecord) => void,
  totalAmount?: number | null,
  isTotalLoading?: boolean
): DataTableColumn[] => {
  const total = (
    <div className='flex items-center gap-2'>
      <span className='text-lg font-bold'>&#931;</span>
      <span className='font-semibold text-main'>
        {isTotalLoading ? 'Đang tính...' : typeof totalAmount === 'number' ? formatRoundedNumber(totalAmount) : '—'}
      </span>
    </div>
  );

  return buildDataTableCols(
    DATA_TABLE_BASE_COLS.map(col => {
      if (col.field === 'so_ct') {
        return {
          ...col,
          renderCell: params => (
            <button
              onClick={e => {
                e.stopPropagation();
                handleViewClick(params.row);
              }}
              className='hover:text-blue-500 hover:underline'
            >
              {params.row?.so_ct}
            </button>
          )
        };
      }

      if (col.field === 't_tt_nt') {
        return { ...col, filter: { type: 'text' as const, after: total } };
      }

      return col;
    })
  );
};

export const getInputTableColumns = (): DataTableColumn[] =>
  buildInputTableCols<HoaDonMuaDichVuRow>([
    {
      field: 'ma_dv',
      headerName: 'Mã dịch vụ',
      width: 150,
      renderCell: params => params.row.ma_dv_data?.ma_dv
    },
    {
      field: 'ten_dv',
      headerName: 'Tên dịch vụ',
      width: 200,
      renderCell: params => params.row.ma_dv_data?.ten_dv
    },
    {
      field: 'tk_vt',
      headerName: 'Tk nợ',
      width: 120,
      renderCell: params => params.row.tk_vt_data?.code
    },
    {
      field: 'ten_tk',
      headerName: 'Tên tài khoản',
      width: 200,
      renderCell: params => params.row.tk_vt_data?.name
    },
    {
      field: 'dien_giai',
      headerName: 'Diễn giải/Tên hàng hóa dịch vụ',
      width: 250,
      renderCell: params => params.row.dien_giai || params.row.ma_dv_data?.ten_dv
    },
    {
      field: 'dvt',
      headerName: 'Đvt',
      width: 100,
      renderCell: params => params.row.dvt_data?.dvt
    },
    {
      field: 'so_luong',
      headerName: 'Số lượng',
      width: 120,
      columnType: 'number',
      renderCell: params => formatNumber(params.row.so_luong || 0)
    },
    {
      field: 'gia_nt',
      headerName: 'Giá VND',
      width: 180,
      columnType: 'number',
      renderCell: params => formatMoney(params.row.gia_nt || 0)
    },
    {
      field: 'tien_nt',
      headerName: 'Tiền VND',
      width: 180,
      columnType: 'number',
      renderCell: params => formatMoney(params.row.tien_nt || 0)
    },
    {
      field: 'ma_thue',
      headerName: 'Thuế suất',
      width: 180,
      renderCell: params => params.row.ma_thue_data?.ten_thue
    },
    {
      field: 'ma_bp',
      headerName: 'Bộ phận',
      width: 120,
      renderCell: params => params.row.ma_bp_data?.ma_bp
    },
    {
      field: 'ma_vv',
      headerName: 'Vụ việc',
      width: 120,
      renderCell: params => params.row.ma_vv_data?.ma_vv
    },
    {
      field: 'ma_hd',
      headerName: 'Hợp đồng',
      width: 120,
      renderCell: params => params.row.ma_hd_data?.ma_hd
    },
    {
      field: 'ma_ku',
      headerName: 'Khế ước',
      width: 120,
      renderCell: params => params.row.ma_ku_data?.ma_ku
    },
    {
      field: 'ma_phi',
      headerName: 'Phí',
      width: 120,
      renderCell: params => params.row.ma_phi_data?.ma_phi
    }
  ]);

export const historyColumns: DataTableColumn[] = [
  { field: 'username', headerName: 'Tài khoản', width: 100 },
  { field: 'nickname', headerName: 'Tên tài khoản', width: 200 },
  { field: 'action', headerName: 'Hành động', width: 100 },
  { field: 'datetime0', headerName: 'Thời gian', width: 150 }
];
